import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plane, ArrowRight, Clock } from "lucide-react";
import { CITIES, POPULAR } from "../data/cities";
import { CITIES as ALL_CITIES, buildPage, cityName } from "../data/seoData";
import { computeFare } from "../data/pricing";
import { useLang } from "../i18n/LanguageContext";
import { useSeo } from "../lib/seo";
import Reveal from "../components/Reveal";
import BookingWidget from "../components/BookingWidget";
import { useMemo } from "react";

const RT = {
  de: { kicker: "Routen", title: "Beliebte Flughafentransfer-Routen ab Zürich.", sub: "Fixpreis-Taxi & Privattransfers auf den meistgebuchten Routen ab Flughafen Zürich (ZRH) — Distanz, Fahrzeit und Preis auf einen Blick." },
  en: { kicker: "Routes", title: "Popular airport transfer routes from Zürich.", sub: "Fixed-fare taxi & private transfers on the most-booked routes from Zürich Airport (ZRH) — see distance, travel time and price at a glance." },
  fr: { kicker: "Itinéraires", title: "Itinéraires de transfert populaires depuis Zurich.", sub: "Taxi à prix fixe & transferts privés sur les itinéraires les plus réservés depuis l'aéroport de Zurich (ZRH) — distance, durée et prix en un coup d'œil." },
  it: { kicker: "Tratte", title: "Tratte di transfer popolari da Zurigo.", sub: "Taxi a tariffa fissa e transfer privati sulle tratte più prenotate dall'aeroporto di Zurigo (ZRH) — distanza, tempo e prezzo a colpo d'occhio." },
};

// Popular list first, then the rest of the cities appended.
const ROUTES = [...POPULAR, ...CITIES.filter((c) => !POPULAR.some((p) => p.slug === c.slug))];

const Routen = () => {
  const { lang } = useLang();
  const r = RT[lang] || RT.de;
  const lng = ["de", "en", "fr", "it"].includes(lang) ? lang : "de";
  const navigate = useNavigate();
  useSeo({ title: `${r.title} | Airport Taxi Zürich`, description: r.sub, path: "/ziele" });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const IDX = { de: { title: "Alle Gemeinden A–Z", sub: "Flughafentransfer Zürich zu allen Schweizer Gemeinden – wählen Sie Ihr Ziel." }, en: { title: "All municipalities A–Z", sub: "Zurich airport transfer to every Swiss municipality – choose your destination." }, fr: { title: "Toutes les communes A–Z", sub: "Transfert aéroport de Zurich vers chaque commune suisse – choisissez votre destination." }, it: { title: "Tutti i comuni A–Z", sub: "Transfer aeroporto di Zurigo verso ogni comune svizzero – scegli la tua destinazione." } };
  const ix = IDX[lng];
  const alpha = useMemo(() => {
    const g = {};
    ALL_CITIES.forEach((c) => {
      const name = cityName(c, lng);
      const L = (name[0] || "#").toUpperCase();
      (g[L] = g[L] || []).push(c);
    });
    return Object.keys(g).sort().map((L) => ({ L, cities: g[L].sort((a, b) => cityName(a, lng).localeCompare(cityName(b, lng))) }));
  }, [lng]);

  return (
    <div className="pt-[68px] min-h-screen bg-white overflow-x-hidden" data-testid="routes-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <Reveal>
          <p className="font-body text-sm text-[#2B4BFF] font-semibold mb-3">{r.kicker}</p>
          <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#0B1533]">{r.title}</h1>
          <p className="font-body text-base sm:text-lg text-[#5A607A] mt-5 max-w-2xl leading-relaxed">{r.sub}</p>
        </Reveal>

        <BookingWidget className="mt-8" />

        <div className="mt-10 space-y-3" data-testid="routes-list">
          {ROUTES.map((c, i) => (
            <Reveal key={c.slug} delay={Math.min(i, 8) * 40}>
              <button onClick={() => navigate(c.path)} className="group w-full flex items-center gap-3 rounded-2xl border border-[#E9E5DE] bg-white px-4 sm:px-5 py-4 hover:border-[#2B4BFF] hover:elev-soft transition-all text-left" data-testid={`route-${c.slug}`}>
                <Plane size={18} className="text-[#2B4BFF] shrink-0" />
                <span className="flex items-baseline gap-2 min-w-0 flex-1">
                  <span className="font-heading font-semibold text-[#0B1533]">Zürich (ZRH)</span>
                  <ArrowRight size={15} className="text-[#9CA0B0] shrink-0" />
                  <span className="font-heading font-semibold text-[#0B1533] truncate">{c.name}</span>
                </span>
                <span className="hidden sm:flex items-center gap-1 font-body text-xs text-[#5A607A] shrink-0"><Clock size={12} /> ~{c.min} min</span>
                <span className="font-heading font-bold text-[#0B1533] shrink-0 ml-1">CHF {computeFare("economy", c.km)}</span>
                <ArrowRight size={16} className="text-[#2B4BFF] group-hover:translate-x-1 transition-transform shrink-0" />
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Full A–Z index of all Swiss municipalities → canonical Landing pages (crawlable) */}
      <div className="border-t border-[#E9E5DE] bg-[#F5F2ED]" data-testid="ziele-index">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#0B1533] leading-tight">{ix.title}</h2>
          <p className="font-body text-base text-[#5A607A] mt-3 max-w-2xl">{ix.sub}</p>
          <p className="font-body text-sm text-[#9CA0B0] mt-2">{ALL_CITIES.length} Gemeinden</p>
          <div className="mt-8 space-y-8">
            {alpha.map((grp) => (
              <div key={grp.L} id={`letter-${grp.L}`}>
                <h3 className="font-heading font-bold text-xl text-[#2B4BFF] mb-3">{grp.L}</h3>
                <div className="columns-2 sm:columns-3 lg:columns-4 gap-4">
                  {grp.cities.map((c) => (
                    <a key={c.s} href={`/${buildPage(lng, "transfer", c).slug}`} className="block font-body text-sm text-[#3d4257] hover:text-[#2B4BFF] py-1 break-inside-avoid transition-colors" data-testid={`ziele-link-${c.s}`}>
                      {cityName(c, lng)}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Routen;
